import React, { useState } from "react";
import {
  Handle,
  Position,
  useReactFlow,
  useNodeId,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import {
  Input,
  Label,
  Button,
  Col,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Row,
} from "reactstrap";
import NotificationMockup from "../../AirAnalytics/NotificationMockup";
const appIcon = require("../../../../assets/images/AirHomeNotification.png");

export type NotificationButton = {
  id: string;
  text: string;
  url: string;
};

export type CustomNotificationNodeData = {
  title: string;
  body: string;
  image?: string;
  link?: string;
  buttons?: NotificationButton[];
};

export type CustomNotificationNodeType = Node<
  CustomNotificationNodeData,
  "customNotification"
>;

function CustomNotificationNode(props: NodeProps<CustomNotificationNodeType>) {
  const { data } = props;
  const nodeId = useNodeId();
  const { setNodes, setEdges, getNodes } = useReactFlow();
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [showMore, setShowMore] = useState(
    Boolean(
      data.image?.trim() ||
        data.link?.trim() ||
        (data.buttons?.[0] &&
          (data.buttons[0].text?.trim() || data.buttons[0].url?.trim())),
    ),
  );

  const update = <K extends keyof CustomNotificationNodeData>(
    field: K,
    value: CustomNotificationNodeData[K],
  ) => {
    setNodes(
      getNodes().map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, [field]: value } } : n,
      ),
    );
  };

  const singleButton = (data.buttons ?? [])[0] ?? {
    id: "btn1",
    text: "",
    url: "",
  };

  const updateButton = (field: keyof NotificationButton, value: string) => {
    const next = { ...singleButton, [field]: value };
    update("buttons", next.text || next.url ? [next] : []);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const id = nodeId ?? "";
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  };

  const closeOffcanvas = () => setOffcanvasOpen(false);

  const hasConfiguredNotification = Boolean(
    (data.title ?? "").trim() ||
      (data.body ?? "").trim() ||
      (data.image ?? "").trim(),
  );
  const previewTitle = (data.title ?? "").trim() || "Notification title";
  const previewBody = (data.body ?? "").trim() || "Notification content";
  const previewImageUrl = (data.image ?? "").trim();

  return (
    <>
      <div
        style={{
          padding: "8px 10px",
          minWidth: "200px",
          maxWidth: "200px",
          background: "#f0fdf4",
          border: "1px solid #86efac",
          borderRadius: "6px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          position: "relative",
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="custom-in"
          style={{ top: "50%" }}
        />
        <button
          type="button"
          onClick={handleRemove}
          className="nodrag nopan"
          aria-label="Remove node"
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 16,
            height: 16,
            padding: 0,
            border: "none",
            borderRadius: 3,
            background: "rgba(0,0,0,0.06)",
            color: "#374151",
            cursor: "pointer",
            fontSize: 12,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <box-icon name="trash" size="12px" color="#454545"></box-icon>
        </button>
        <div
          style={{
            fontWeight: 600,
            fontSize: 11,
            marginBottom: 6,
            color: "#166534",
            cursor: "grab",
            paddingRight: 22,
          }}
        >
          Custom Notification
        </div>
        <div className="nodrag">
          {hasConfiguredNotification ? (
            <div
              style={{
                marginBottom: 8,
                padding: 8,
                background: "#ffffff",
                borderRadius: 10,
                border: "1px solid #e6e1e4",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  gap: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "20%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={appIcon.default || appIcon}
                      alt="App Icon"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#111827",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    AirHome
                  </span>
                </div>
                <span style={{ fontSize: 9, color: "#6b7280", flexShrink: 0 }}>
                  now
                </span>
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#111827",
                  marginBottom: 2,
                }}
              >
                {previewTitle}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#374151",
                }}
              >
                {previewBody}
              </div>
              {previewImageUrl ? (
                <img
                  src={previewImageUrl}
                  alt="Notification"
                  style={{
                    width: "100%",
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 10,
                    marginTop: 8,
                    display: "block",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </div>
          ) : (
            <div
              style={{
                marginBottom: 8,
                fontSize: 10,
                color: "#64748b",
                padding: "2px 0",
              }}
            >
              Add custom notification details to show preview in node.
            </div>
          )}
          <Button
            type="button"
            color="light"
            size="sm"
            outline
            className="w-100 d-flex align-items-center justify-content-center"
            style={{
              fontSize: 10,
              minHeight: 26,
              border: "1px dashed #adb5bd",
              borderRadius: 6,
              fontWeight: 500,
              color: "#495057",
              backgroundColor: "#f8f9fa",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOffcanvasOpen(true);
            }}
          >
            {hasConfiguredNotification
              ? "Edit notification"
              : "Add notification"}
          </Button>
        </div>
      </div>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        style={{ width: "80%" }}
        className="nodrag nopan border-start"
      >
        <OffcanvasHeader
          toggle={closeOffcanvas}
          className="d-flex align-items-center justify-content-between  "
        >
          <span className="fw-semibold">Custom Notification</span>
        </OffcanvasHeader>
        <OffcanvasBody>
          <Row>
            <Col md={8}>
              <Row>
                <Col md={6} className="mb-3">
                  <Label>Title</Label>
                  <Input
                    type="text"
                    placeholder="Notification title"
                    value={data.title ?? ""}
                    onChange={(e) => update("title", e.target.value)}
                  />
                </Col>
                <Col md={6} className="mb-3">
                  <Label>Link</Label>
                  <Input
                    type="text"
                    placeholder="https://..."
                    value={data.link ?? ""}
                    onChange={(e) => update("link", e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    placeholder="Message body"
                    value={data.body ?? ""}
                    onChange={(e) => update("body", e.target.value)}
                  />
                </Col>
              </Row>
              <Button
                type="button"
                color="link"
                size="sm"
                className="p-0 mb-2 text-secondary"
                style={{ fontSize: 12 }}
                onClick={() => setShowMore((v) => !v)}
              >
                {showMore ? "− Less options" : "+ Add more options"}
              </Button>
              {showMore && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Label>Image URL</Label>
                    <Input
                      type="text"
                      placeholder="https://..."
                      value={data.image ?? ""}
                      onChange={(e) => update("image", e.target.value)}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Button Text</Label>
                    <Input
                      type="text"
                      placeholder="Button text"
                      value={singleButton.text}
                      onChange={(e) => updateButton("text", e.target.value)}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Button URL</Label>
                    <Input
                      type="text"
                      placeholder="https://..."
                      value={singleButton.url}
                      onChange={(e) => updateButton("url", e.target.value)}
                    />
                  </Col>
                </Row>
              )}
            </Col>
            <Col md={4}>
              <div className="mb-3">
                {data.title?.trim() ||
                data.body?.trim() ||
                data.image?.trim() ? (
                  <NotificationMockup
                    templateData={{
                      headings: data.title || "Title",
                      contents: data.body || "content",
                      global_image: data.image || null,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#999",
                      background: "#f5f5f7",
                      borderRadius: "8px",
                    }}
                  >
                    Fill in the form to see preview
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </OffcanvasBody>
        <div
          className="d-flex justify-content-end border-top"
          style={{ gap: 8, padding: "12px 16px" }}
        >
          <Button className="btn btn-dark" onClick={closeOffcanvas} size="sm">
            Done
          </Button>
        </div>
      </Offcanvas>
    </>
  );
}

export default CustomNotificationNode;
